<!--
  worldMap
  D3JS 기반의 세계지도 뷰-컴포넌트 입니다. 플러그인에 등록되어 전역에서 디렉티브로 사용할 수 있습니다. 생성된 세계지도
  인스턴스는 독립되어 동작합니다.

  @since 2018-03-19
  @author ms1124.kim
  @version 0.0.
  @revision history
  - 2018.03.19 김민수 : 컴포넌트 정의
  - 2018.03.20 김민수 : 기능별 주석 추가
  - 2018.03.22 김민수 : Resize 기능 재정의 - Redraw로 변경
  - 2018.03.23 김민수 : Resize 기능 재정의 - Redraw + CSS로 변경
  - 2018.06.08 김민수 : 커스텀 컬러 및 컬러 업데이트 기능정의
-->

<template>
  <div ref="worldmap"
       itemid="worldmap-content"
       class="worldmap-inner-container"
  >
  </div>
</template>
<script>
  import d3Figure from './d3Figure.js'

  export default {
    name: 'worldMap',
    components: {},
    directives: {},
    mixins: [],
    data () {
      return {
        svg: null,
        options: {},
        geoPath: null,
        mapWidth: 0,
        mapHeight: 0,
        innerMemory: {
          text: {},
          line: {},
          image: {},
          circle: {},
          marker: {},
          worldmap: {}
        },
        geoProjection: null,
        defaultOptions: {
          text: {
            'text': '',
            'dx': 0,
            'dy': 0,
            'offsetX': 0,
            'offsetY': 0,
            'fill': 'black',
            'class': '',
            'font-size': 10,
            'font-weight': 'bold',
            'text-anchor': 'middle',
            'click': function (e, data) {},
            'mouseout': function (e, data) {},
            'mouseover': function (e, data) {},
            'mousemove': function (e, data) {}
          },
          line: {
            'fill': 'none',
            'class': '',
            'stroke': 'red',
            'stroke-width': '2px',
            'srcOffsetX': 0,
            'srcOffsetY': 0,
            'dstOffsetX': 0,
            'dstOffsetY': 0,
            'direction': 'oneway',
            'click': function (e, data) {},
            'mouseout': function (e, data) {},
            'mouseover': function (e, data) {},
            'mousemove': function (e, data) {}
          },
          image: {
            'data': [],
            'class': '',
            'offsetX': 0,
            'offsetY': 0,
            'width': 50,
            'height': 50,
            'click': function (e, data) {},
            'mouseout': function (e, data) {},
            'mouseover': function (e, data) {},
            'mousemove': function (e, data) {}
          },
          circle: {
            'size': 50,
            'fill': 'navy',
            'fill-opacity': '1.0',
            'title': '',
            'class': '',
            'offsetX': 0,
            'offsetY': 0,
            'stroke': 'white',
            'stroke-width': '2px',
            'text': {
              'fill': 'white',
              'font-size': 10,
              'font-weight': 'bold'
            },
            'click': function (e, data) {},
            'mouseout': function (e, data) {},
            'mouseover': function (e, data) {},
            'mousemove': function (e, data) {}
          },
          marker: {
            type: 'arrow',
            arrow: {
              d: {
                start: 'M 0 0 L 11 -5 L 10 5Z',
                end: 'M 0 -5 L 10 0 L 0 5'
              },
              refX: {
                start: 2,
                end: 5
              },
              refY: {
                start: 0,
                end: 0
              },
              fill: 'red',
              orient: 'auto',
              viewBox: {
                start: '0 -5 10 10',
                end: '0 -5 10 10'
              },
              markerWidth: 5,
              markerHeight: 5
            }
          },
          worldmap: {
            'click': function (e, data) {},
            'mouseout': function (e, data) {},
            'mouseover': function (e, data) {},
            'mousemove': function (e, data) {}
          }
        },
        redrawElement: {
          line: [],
          text: [],
          image: [],
          circle: [],
          tooltip: []
        }
      }
    },
    computed: {},
    props: {
      click: {
        type: Function,
        default: function () {
          return null
        }
      },
      mouseout: {
        type: Function,
        default: function () {
          return null
        }
      },
      mouseover: {
        type: Function,
        default: function () {
          return null
        }
      },
      mousemove: {
        type: Function,
        default: function () {
          return null
        }
      },
      codeFormat: {
        type: String,
        default: 'iso_a2'
      },
      tooltipEl: {
        type: Array,
        default () {
          return []
        }
      },
      colorMappingByValue: {
        type: Function,
        default () {
          return '#cae3f5'
        }
      }
    },
    beforeCreate () {},
    created () {
      // 브라우저의 Resize 이벤트를 컴포넌트의 인스턴스 메서드에 연결합니다.
      this._onResizeWorldMap = _.throttle(this.onResizeWorldMap, 500)

      // 리사이즈 이벤트 연결
      window.addEventListener('resize', this._onResizeWorldMap)
    },
    beforeMount () {
      // Redraw 데이터 초기화
      this.redrawElement.line = []
      this.redrawElement.text = []
      this.redrawElement.image = []
      this.redrawElement.circle = []
      this.redrawElement.tooltip = []
    },
    mounted () {
      // DOM 마운트가 완료되면 세계지도를 화면에 생성합니다. 이 때, 부모 요소의 크기정보를 인자로 전달 받는다.
      // 세계 지도가 그려질 요소를 가져옵니다.
      const worldMap = this.$refs.worldmap

      // 세계 지도에 맵핑할 옵션을 정의합니다.
      const worldMapOptions = {
        click: this.click,
        mouseout: this.mouseout,
        mouseover: this.mouseover,
        mousemove: this.mousemove,
        codeFormat: this.codeFormat,
        colorMappingByValue: this.colorMappingByValue
      }

      // 2018.05.21 추가 - Props로 전달된 Tooltip ID를 추가한다.
      this.redrawElement.tooltip = this.tooltipEl

      // 세계 지도를 생성하고 마우스 이벤트를 적용합니다.
      d3Figure.initWorldMap(this, worldMap.clientWidth, worldMap.clientHeight, worldMapOptions)
    },
    beforeUpdate () {},
    updated () {},
    activated () {},
    deactivated () {},
    beforeDestroy () {
      // 데이터 초기화
      this.redrawElement.line = []
      this.redrawElement.text = []
      this.redrawElement.image = []
      this.redrawElement.circle = []
      this.redrawElement.tooltip = []

      // 세계지도 객체 제거
      d3Figure.destroyWorldMap(this)

      // 연결된 Resize 이벤트를 제거합니다.
      window.removeEventListener('resize', this._onResizeWorldMap)

      // 리사이즈 객체 초기화
      this._onResizeWorldMap = null
    },
    methods: {
      drawText (target, options, updateTarget) {
        /* SVG 텍스트 요소를 화면에 출력합니다. 파라미터는 텍스트 요소가 출력될 국가코드, 텍스트 옵션, 업데이트 할 요소
        Name 속성입니다.
        */
        // 텍스트 요소 생성
        const elementId = d3Figure.drawText(this, target, options, updateTarget)
        let redrawData = this.redrawElement.text

        // Redraw 옵션 객체 생성
        const redrawOptions = {
          itemId: elementId,
          target: target,
          options: options
        }

        // ItemId가 리턴되었고 업데이트가 아니라면 정보 추가
        if (elementId && !updateTarget) {
          // 중복되는 itemId가 있는지 확인
          for (let i = 0, max = redrawData.length; i < max; i++) {
            const redrawElement = redrawData[i]
            if (redrawElement.itemId === elementId) {
              return
            }
          }

          // Redraw 목록에 추가
          redrawData.push(redrawOptions)
        }

        // ItemId가 리턴되었고 업데이트라면 정보 업데이트
        if (elementId === updateTarget) {
          // redraw 대상 목록에서 검색
          for (let i = 0, max = redrawData.length; i < max; i++) {
            // 아이디가 동일하면 옵션 변경
            if (redrawData[i].itemId === elementId) {
              // 옵션 업데이트
              redrawData[i].options = {
                ...redrawData[i].options,
                ...redrawOptions.options
              }
              break
            }
          }
        }
      },
      drawImage (target, imagePath, options, updateTarget) {
        /* 이미지 요소를 화면에 출력합니다. 파라미터는 이미지 요소가 출력될 국가코드, 이미지 옵션, 업데이트 할 요소
        Name 속성입니다.
        */
        let redrawData = this.redrawElement.image
        const elementId = d3Figure.drawImage(this, target, imagePath, options, updateTarget)

        // Redraw 옵션 객체 생성
        const redrawOptions = {
          itemId: elementId,
          path: imagePath,
          target: target,
          options: options
        }

        // ItemId가 리턴되었고 업데이트가 아니라면 정보 추가
        if (elementId && !updateTarget) {
          // 중복되는 itemId가 있는지 확인
          for (let i = 0, max = redrawData.length; i < max; i++) {
            const redrawElement = redrawData[i]
            if (redrawElement.itemId === elementId) {
              return
            }
          }

          // Redraw 목록에 추가
          redrawData.push(redrawOptions)
        }

        // ItemId가 리턴되었고 업데이트라면 정보 업데이트
        if (elementId === updateTarget) {
          // redraw 대상 목록에서 검색
          for (let i = 0, max = redrawData.length; i < max; i++) {
            // 아이디가 동일하면 옵션 변경
            if (redrawData[i].itemId === elementId) {
              // 이미지 경로 업데이트
              redrawData[i].path = redrawOptions.path

              // 옵션 업데이트
              redrawData[i].options = {
                ...redrawData[i].options,
                ...redrawOptions.options
              }
              break
            }
          }
        }
      },
      drawCircle (target, options, updateTarget) {
        /* 원형 도형을 화면에 출력합니다. 파라미터는 원형 도형 요소가 출력될 국가코드, 도형 옵션, 업데이트 할 요소
        Name 속성입니다.
        */
        let redrawData = this.redrawElement.circle

        const elementId = d3Figure.drawCircle(this, target, options, updateTarget)

        // Redraw 옵션 객체 생성
        const redrawOptions = {
          itemId: elementId,
          target: target,
          options: options
        }

        // ItemId가 리턴되었고 업데이트가 아니라면 정보 추가
        if (elementId && !updateTarget) {
          // 중복되는 itemId가 있는지 확인
          for (let i = 0, max = redrawData.length; i < max; i++) {
            const redrawElement = redrawData[i]
            if (redrawElement.itemId === elementId) {
              return
            }
          }

          // Redraw 목록에 추가
          redrawData.push(redrawOptions)
        }

        // ItemId가 리턴되었고 업데이트라면 정보 업데이트
        if (elementId === updateTarget) {
          // redraw 대상 목록에서 검색
          for (let i = 0, max = redrawData.length; i < max; i++) {
            if (redrawData[i].itemId === elementId) {
              // 이미지 옵션 업데이트
              redrawData[i].options = {
                ...redrawData[i].options,
                ...redrawOptions.options
              }
              break
            }
          }
        }
      },
      drawArrowLine (source, destination, options) {
        /* 화살표 곡선을 화면에 출력합니다. 파라미터는 출발지 국가, 도착지 국가, 화살표 곡선 옵션입니다. 화살표 곡선은
        업데이트를 지원하지 않습니다. - 필요 시 지원 예정
        */
        let redrawElement = this.redrawElement.line
        const elementId = d3Figure.drawArrowLine(this, source, destination, options)

        // Redraw 옵션 객체 생성
        const redrawOptions = {
          itemId: elementId,
          src: source,
          dst: destination,
          options: options
        }

        // ItemId가 리턴되었고 업데이트가 아니라면 정보 추가
        if (elementId) {
          // 중복되는 itemId가 있는지 확인
          for (let i = 0, max = redrawElement.length; i < max; i++) {
            if (redrawElement[i].itemId === elementId) {
              return
            }
          }

          // Redraw 목록에 추가
          redrawElement.push(redrawOptions)
        }
      },
      onResizeWorldMap () {
        d3Figure.redrawWorldMap(this, this.$el.clientWidth, this.$el.clientHeight, this.redrawElement)
      },
      getSvgMousePosition () {
        return d3Figure.getSvgMousePosition(this)
      },
      selectCountryByCode (target, isoType = 'iso_a2') {
        // 전달된 국가코드를 맵 데이터에서 검색하여 국가를 선택합니다.
        return d3Figure.selectCountryByCode(this, target, isoType)
      },
      changeWorldmapColor (colorMappingByValue = null) {
        /* 파라미터로 전달된 컬러맵핑 콜백함수가 있을경우, 콜백함수를 색상변경 메서드로 전달합니다. 만약, 파라미터가 없고
        옵션으로 colorMappingByValue가 전달된 경우에는 옵션 메서드에 따라 색상을 변경합니다.
        */
        d3Figure.changeWorldmapColor(this, colorMappingByValue)
      },
      removeWorldmapElement (target) {
        /* 세계지도 위에 그려진 요소들을 제거합니다. 파라미터로 요소의 Name이 전달되면 특정 요소만 제거하며 없을 경우,
        세계지도를 제외한 모든 요소를 제거합니다.
        */

        // 지우려는 대상이 지목되면 검색 후 제거
        if (target) {
          for (const key in this.redrawElement) {
            let idx = 0
            for (const el of this.redrawElement[key]) {
              if (el.itemId === target) {
                // Redraw 대상 아이템 제거
                el.slice(idx)

                // 실제 Element 제거
                d3Figure.removeFigureElement(this, target)

                // 반복문 종료
                return
              }

              // 인덱스 증가
              idx++
            }
          }
        } else {
          this.redrawElement.text = []
          this.redrawElement.line = []
          this.redrawElement.image = []
          this.redrawElement.circle = []
          this.redrawElement.tooltip = []
        }

        d3Figure.removeFigureElement(this, target)
      },
      /*
        이름: isCountryPathByCode
        설명: 현재 국가코드가 지도상 Path로 표현이 되어있는지 확인합니다.
      */
      isCountryPathByCode (code) {
        return d3Figure.isCountryPathByCode(this, code)
      }
    },
    watch: {}
  }
</script>
<style scoped>
</style>
